import {useQuery} from '@tanstack/react-query'
import {Activity, Clock} from 'lucide-react'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {TextLink} from '@/components/shared/TextLink'
import {IconText} from '@/components/shared/IconText'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {Stack} from '@/components/shared/Stack'
import {StatusText} from '@/components/shared/StatusText'
import {TimelineItem} from '@/components/shared/TimelineItem'
import {formatTimestamp} from '@/lib/date-utils'
import {routes} from '@/lib/routes'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {RestrictionRuleDetectionStatus} from '@/types/restriction-rule'

interface DetectionHistoryCardProps {
    ruleId: string
}

const PAGE_SIZE = 5

/**
 * Sidebar card listing the most recent violation-detection runs for a rule.
 *
 * Mirrors the auto-assign Execution History card (see {@link AutoAssignRuleDetail}).
 * Renders four states: loading, error, empty, populated. The "View all" link
 * routes to the detection-history list page (added in 245.06).
 */
export function DetectionHistoryCard({ruleId}: DetectionHistoryCardProps) {
    const services = useServices()
    const i18n = useI18n()

    const {data: page, isLoading, isError} = useQuery({
        queryKey: ['restriction-rule-detections', ruleId, PAGE_SIZE],
        queryFn: () => services.restrictionRules.detections(ruleId, {size: PAGE_SIZE}),
        enabled: !!ruleId,
    })

    const detections = page?.content ?? []

    return (
        <Card data-testid="detection-history-card">
            <CardHeader>
                <CardTitle className="text-base" data-testid="card-title-detection-history">
                    <IconText icon={Activity} size="sm">
                        {i18n.restrictionRules.detectionHistory}
                    </IconText>
                </CardTitle>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <MutedText size="sm" data-testid="detection-loading">
                        {i18n.restrictionRules.detectionLoading}
                    </MutedText>
                ) : isError ? (
                    <MutedText size="sm" data-testid="detection-error">
                        {i18n.restrictionRules.detectionLoadFailed}
                    </MutedText>
                ) : detections.length === 0 ? (
                    <MutedText size="sm" data-testid="detection-empty">
                        {i18n.restrictionRules.noDetectionsYet}
                    </MutedText>
                ) : (
                    <Stack gap="sm">
                        {detections.map((entry) => (
                            <TimelineItem key={entry.id} data-testid={`detection-row-${entry.id}`}>
                                <InlineRow gap="md">
                                    <Badge
                                        variant={
                                            entry.status === RestrictionRuleDetectionStatus.SUCCESS
                                                ? BadgeVariant.SUCCESS
                                                : BadgeVariant.DESTRUCTIVE
                                        }
                                        className="text-xs"
                                        data-testid={`detection-status-${entry.id}`}
                                    >
                                        {entry.status === RestrictionRuleDetectionStatus.SUCCESS
                                            ? i18n.restrictionRules.detectionStatusSuccess
                                            : i18n.restrictionRules.detectionStatusFailed}
                                    </Badge>
                                    <Badge variant="outline" className="text-xs">
                                        {i18n.common.versionPrefix({version: entry.ruleVersion})}
                                    </Badge>
                                </InlineRow>
                                <InlineRow gap="md" className="mt-1">
                                    <IconText icon={Clock} size="sm">
                                        <MutedText size="xs">
                                            {formatTimestamp(entry.detectionStartedAt)}
                                        </MutedText>
                                    </IconText>
                                </InlineRow>
                                <InlineRow gap="md" className="mt-1 text-xs">
                                    <MutedText size="xs">
                                        {i18n.restrictionRules.detectionIdentitiesEvaluated}
                                        {' '}
                                        <span data-testid={`detection-evaluated-${entry.id}`}>
                                            {entry.identitiesEvaluated}
                                        </span>
                                    </MutedText>
                                    <StatusText
                                        tone="danger"
                                        data-testid={`detection-found-${entry.id}`}
                                    >
                                        {i18n.restrictionRules.detectionViolationsFound}
                                        {' '}
                                        {entry.violationsFound}
                                    </StatusText>
                                    <StatusText
                                        tone="success"
                                        data-testid={`detection-resolved-${entry.id}`}
                                    >
                                        {i18n.restrictionRules.detectionViolationsResolved}
                                        {' '}
                                        {entry.violationsResolved}
                                    </StatusText>
                                </InlineRow>
                                {entry.status === RestrictionRuleDetectionStatus.FAILED && (
                                    <InlineRow gap="md" className="mt-1 text-xs">
                                        <TextLink
                                            testId={`detection-error-link-${entry.id}`}
                                            to={routes.admin.errorLogs.byCorrelation(entry.id)}
                                        >
                                            {i18n.restrictionRules.detectionViewErrors}
                                        </TextLink>
                                    </InlineRow>
                                )}
                            </TimelineItem>
                        ))}
                        {page && page.totalElements > PAGE_SIZE && (
                            <TextLink
                                testId="link-view-all-detections"
                                to={routes.accessGovernance.restrictionRules.detectionsForRule(ruleId)}
                            >
                                <MutedText size="xs">
                                    {i18n.restrictionRules.viewAllDetections({count: page.totalElements})}
                                </MutedText>
                            </TextLink>
                        )}
                    </Stack>
                )}
            </CardContent>
        </Card>
    )
}
